// Studio suspension: a deliberate superUser lockout of one studio while it is
// unpaid (or otherwise inactive). Pure helpers with no Prisma so proxy.js
// (edge runtime), the root layout, the API token validators and client
// components all decide "is this session locked out?" the same way.
//
// The switch is `Workspace.suspendedAt`. It is set only by the developer-only
// action in app/(superUser)/admin/superUser/studios/actions.js — never derived
// from `businessStatus`, which is bookkeeping and must not lock anyone out.

import { isDeveloperUserId } from "@/lib/feature-access";

export const STUDIO_SUSPENDED_PATH = "/studio-suspended";
export const STUDIO_SUSPENDED_CODE = "STUDIO_SUSPENDED";
export const STUDIO_SUPPORT_EMAIL = "support@salesably.ai";
export const STUDIO_SUSPENDED_MESSAGE =
  "This studio is currently inactive. Contact Salesably to reactivate it.";

// Paths that keep working for a suspended session: the notice itself and the
// auth endpoints that end the session or refresh its token.
const SUSPENSION_EXEMPT_PATH_PREFIXES = [STUDIO_SUSPENDED_PATH, "/api/auth"];

export function isWorkspaceSuspended(workspace) {
  return Boolean(workspace?.suspendedAt);
}

// SuperUsers keep full access for support and reactivation — but only while
// acting as themselves. Impersonating a member shows what that member sees,
// and the impersonation banner stays available to step back out.
export function isSuspensionExempt(user) {
  return Boolean(user?.superUser) && !user?.isImpersonated;
}

// Session shape from getServerSession/useSession: `workspace` is the active
// studio row, `user` carries superUser/isImpersonated.
export function isStudioAccessSuspended(session) {
  return (
    isWorkspaceSuspended(session?.workspace) &&
    !isSuspensionExempt(session?.user)
  );
}

// JWT variant for proxy.js, which never sees the workspace row: the token's
// `workspaceSuspended` flag is refreshed by lib/auth/session-flags.js.
export function isTokenStudioSuspended(token) {
  return Boolean(token?.workspaceSuspended) && !isSuspensionExempt(token);
}

// Drop suspended studios from a list of {id, name, suspendedAt} rows unless
// the user is exempt. Used to scope MCP/connector access and the "switch to
// another studio" offer on the notice.
export function filterSuspendedStudios(studios = [], user) {
  const list = Array.isArray(studios) ? studios.filter(Boolean) : [];
  if (isSuspensionExempt(user)) return list;
  return list.filter((studio) => !isWorkspaceSuspended(studio));
}

export function isSuspensionExemptPath(pathname = "") {
  if (typeof pathname !== "string" || !pathname) return false;
  return SUSPENSION_EXEMPT_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

// Who may flip the switch: a developer acting as a superUser, not while
// impersonating. Mirrors the "studio-suspension" flag in useFeatureFlags.
export function canSuspendStudios(user) {
  return isSuspensionExempt(user) && isDeveloperUserId(user?.id);
}

export function studioSuspendedPayload() {
  return {
    error: STUDIO_SUSPENDED_MESSAGE,
    code: STUDIO_SUSPENDED_CODE,
    supportEmail: STUDIO_SUPPORT_EMAIL,
  };
}

// 403 for bearer-token API paths (mobile, OAuth, MCP) — plain Response so it
// is usable from the edge runtime and from route handlers alike.
export function studioSuspendedResponse() {
  return new Response(JSON.stringify(studioSuspendedPayload()), {
    status: 403,
    headers: { "Content-Type": "application/json" },
  });
}
